import React, { useCallback } from 'react';
import classNames from 'classnames';
import { EstimateStatus } from '@shared/utils/constants';
import type { GetVesselDetailsResponse } from '@api/features/vessels/getVesselDetails';
import EditableDetails from '@client/components/EditableDetails/EditableDetails';
import { useEstimate } from '@client/screens/estimates/details/context/EstimatorContext';
import getIntakeVesselFields from './helpers/getIntakeVesselFields';
import styles from './IntakeCalculatorModal.module.css';

const IntakeCalculatorKeyDetails = ({
	vesselDetails,
	handleEstimateChange,
	handleVesselChange,
}: {
	vesselDetails: GetVesselDetailsResponse | null;
	handleEstimateChange: (values: Record<string, any>) => void;
	handleVesselChange: (values: Record<string, any>) => void;
}) => {
	const {
		estimate,
		vessel,
	} = useEstimate();

	const noVessel = vessel == null;
	const disabled = estimate?.status === EstimateStatus.Fixed || noVessel;

	const handleCombinedUpdate = useCallback(
		async (values: Record<string, any>) => {
			const [key, value] = Object.entries(values)[0];
			const estimateKeys = ['summerDraftOverride', 'tpcOverride', 'constantsOverride', 'vesselCapacityType', 'dwtOverride'];
			const vesselKeys = ['grainCapacity', 'baleCapacity'];

			if (estimateKeys.includes(key)) {
				if (!estimate?.id) {
					return;
				}

				if ((estimate as any)[key] === value) {
					return;
				}

				handleEstimateChange(values);
			} else if (vesselKeys.includes(key)) {
				if (!vessel?.id) {
					return;
				}

				if (vessel[key as keyof typeof vessel] === value) {
					return;
				}

				const attributes = { [key]: value };

				handleVesselChange(attributes);
			}
		},
		[
			estimate,
			vessel,
			handleEstimateChange,
			handleVesselChange,
		],
	);

	return (
		<div className={classNames(styles.keyDetails, { [styles.noVessel]: noVessel })}>
			<EditableDetails
				layout="horizontal"
				labelWidth={160}
				size="small"
				colSpan={2}
				title="Vessel details"
				// @ts-ignore
				items={getIntakeVesselFields({
					estimate,
					vesselDetails,
					disabled,
				})}
				// @ts-ignore
				onEditValuesChange={handleCombinedUpdate}
				editing
				hideButtons
			/>
		</div>
	);
};

export default IntakeCalculatorKeyDetails;
